import type { SourceContext } from '../core/definitions';
import { SourceLoadError } from './errors';

export interface MediaRef {
  assetId: string;
  url: string;
}

export function hasRef(ref: MediaRef): boolean {
  return ref.assetId !== '' || ref.url !== '';
}

/** Blob for an imported asset, or fetched from a URL (which must allow CORS). */
export async function loadBlob(ctx: SourceContext, ref: MediaRef): Promise<Blob> {
  if (ref.assetId) {
    const blob = await ctx.assets.getBlob(ref.assetId);
    if (!blob) throw new SourceLoadError('not-found', `Asset "${ref.assetId}" was not found`);
    return blob;
  }
  try {
    const res = await fetch(ref.url, { mode: 'cors' });
    if (!res.ok) throw new SourceLoadError('load-failed', `HTTP ${res.status} for ${ref.url}`);
    return await res.blob();
  } catch (err) {
    if (err instanceof SourceLoadError) throw err;
    throw new SourceLoadError('load-failed', `Could not fetch ${ref.url}: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/** Playable URL for an asset or the URL itself. */
export async function resolveUrl(ctx: SourceContext, ref: MediaRef): Promise<string> {
  if (ref.assetId) {
    const url = await ctx.assets.getUrl(ref.assetId);
    if (!url) throw new SourceLoadError('not-found', `Asset "${ref.assetId}" was not found`);
    return url;
  }
  return ref.url;
}

/** Problem with a typed media URL for the properties form, or null when it can be loaded (an empty value clears it). */
export function validateMediaUrl(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return 'Enter a full address starting with https:// or http://';
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return 'Only http:// and https:// addresses can be loaded';
  return null;
}

/** ISO-BMFF sample-entry codes of video tracks, as found inside an `stsd` box. */
const MP4_VIDEO_CODECS = new Set([
  'avc1', 'avc3', 'hvc1', 'hev1', 'dvh1', 'dvhe', 'vp08', 'vp09', 'av01', 'mp4v', 's263',
  'apch', 'apcn', 'apcs', 'apco', 'ap4h', 'ap4x', 'mjpa', 'mjpb', 'jpeg', 'dvc ', 'dvcp', 'mx5n',
]);
/** Bytes scanned at each end of the file: `moov` sits at the start of fast-start files and at the end of the rest. */
const PROBE_WINDOW_BYTES = 4 * 1024 * 1024;

function findAscii(bytes: Uint8Array, needle: string, from = 0): number {
  const first = needle.charCodeAt(0);
  outer: for (let i = from; i <= bytes.length - needle.length; i++) {
    if (bytes[i] !== first) continue;
    for (let j = 1; j < needle.length; j++) if (bytes[i + j] !== needle.charCodeAt(j)) continue outer;
    return i;
  }
  return -1;
}

function videoCodecIn(bytes: Uint8Array): string | null {
  // MP4/MOV: stsd = size(4) 'stsd' version+flags(4) entryCount(4), then each entry is size(4) + code(4).
  for (let at = findAscii(bytes, 'stsd'); at >= 0; at = findAscii(bytes, 'stsd', at + 4)) {
    const code = String.fromCharCode(...bytes.subarray(at + 16, at + 20));
    if (MP4_VIDEO_CODECS.has(code)) return code;
  }
  // Matroska/WebM: a CodecID element (0x86) holding "V_…" names a video track.
  for (let at = findAscii(bytes, 'V_'); at >= 2; at = findAscii(bytes, 'V_', at + 2)) {
    const size = bytes[at - 1];
    if (bytes[at - 2] === 0x86 && size > 0x80 && size < 0xa0) {
      return String.fromCharCode(...bytes.subarray(at, at + (size & 0x7f)));
    }
  }
  return null;
}

/**
 * The codec of the file's first video track, read from the container header
 * (MP4/MOV `stsd`, Matroska/WebM CodecID), or null when there is no video track
 * or the container is not recognised. Lets a player tell "audio-only file" from
 * "video the browser cannot decode", which an element alone reports the same way.
 */
export async function probeVideoCodec(blob: Blob): Promise<string | null> {
  const head = new Uint8Array(await blob.slice(0, PROBE_WINDOW_BYTES).arrayBuffer());
  const found = videoCodecIn(head);
  if (found || blob.size <= PROBE_WINDOW_BYTES) return found;
  return videoCodecIn(new Uint8Array(await blob.slice(Math.max(PROBE_WINDOW_BYTES, blob.size - PROBE_WINDOW_BYTES)).arrayBuffer()));
}
